import { MGPOptional, MGPValidation } from '@everyboard/lib';
import { BoardValue } from '../../jscaip/AI/BoardValue';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord } from '../../jscaip/Coord';
import { GameStatus } from '../../jscaip/GameStatus';
import { NInARowHelper } from '../../jscaip/NInARowHelper';
import { Player } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { TableUtils } from '../../jscaip/TableUtils';
import { TrexoFailure } from './TrexoFailure';
import { TrexoMove } from './TrexoMove';
import { TrexoPieceStack, TrexoState } from './TrexoState';
export class TrexoNode extends GameNode {
}
export class TrexoRules extends Rules {
    static instance = MGPOptional.empty();
    static get() {
        if (TrexoRules.instance.isAbsent()) {
            TrexoRules.instance = MGPOptional.of(new TrexoRules());
        }
        return TrexoRules.instance.get();
    }
    static getOwner(piece) {
        return piece.getOwner();
    }
    static TREXO_HELPER = new NInARowHelper(TrexoRules.getOwner, 5);
    static getSquareScore(state, coord) {
        return TrexoRules.TREXO_HELPER.getSquareScore(state, coord);
    }
    static getVictoriousCoords(state) {
        const victoryOfLastPlayer = [];
        const victoryOfNextPlayer = [];
        const previousPlayer = state.getPreviousPlayer();
        for (const coordAndContent of state.getCoordsAndContents()) {
            // for every column, starting from the bottom of each column
            // while we haven't reached the top or an empty space
            const coord = coordAndContent.coord;
            const pieceOwner = state.getPieceAt(coord).getOwner();
            if (pieceOwner.isPlayer()) {
                const squareScore = TrexoRules.getSquareScore(state, coord);
                if (BoardValue.isVictoryValue(squareScore)) {
                    if (pieceOwner === previousPlayer) {
                        victoryOfLastPlayer.push(coord);
                    }
                    else {
                        victoryOfNextPlayer.push(coord);
                    }
                }
            }
        }
        if (victoryOfNextPlayer.length > 0) {
            return victoryOfNextPlayer;
        }
        else {
            return victoryOfLastPlayer;
        }
    }
    getInitialState() {
        const board = TableUtils.create(TrexoState.SIZE, TrexoState.SIZE, TrexoPieceStack.EMPTY);
        return new TrexoState(board, 0);
    }
    applyLegalMove(move, state, _config, _info) {
        return state
            .drop(move.getZero(), Player.ZERO)
            .drop(move.getOne(), Player.ONE)
            .incrementTurn();
    }
    isLegal(move, state) {
        if (this.isUnevenGround(move, state)) {
            return MGPValidation.failure(TrexoFailure.CANNOT_DROP_PIECE_ON_UNEVEN_GROUNDS());
        }
        if (this.landsOnOnlyOnePiece(move, state)) {
            return MGPValidation.failure(TrexoFailure.CANNOT_DROP_ON_ONLY_ONE_PIECE());
        }
        return MGPValidation.SUCCESS;
    }
    isUnevenGround(move, state) {
        const zero = state.getPieceAt(move.getZero());
        const one = state.getPieceAt(move.getOne());
        return zero.getHeight() !== one.getHeight();
    }
    landsOnOnlyOnePiece(move, state) {
        const zeroSpace = state.getPieceAt(move.getZero());
        const oneSpace = state.getPieceAt(move.getOne());
        if (zeroSpace.getUpperTileId() === -1 && oneSpace.getUpperTileId() === -1) {
            return false;
        }
        return zeroSpace.getUpperTileId() === oneSpace.getUpperTileId();
    }
    getGameStatus(node) {
        const state = node.gameState;
        const previousPlayer = state.getPreviousPlayer();
        let lastPlayerAligned5 = false;
        for (const coordAndContent of state.getCoordsAndContents()) {
            // for every column, starting from the bottom of each column
            // while we haven't reached the top or an empty space
            const coord = coordAndContent.coord;
            const pieceOwner = coordAndContent.content.getOwner();
            if (pieceOwner.isPlayer()) {
                const squareScore = TrexoRules.getSquareScore(state, coord);
                if (BoardValue.isVictoryValue(squareScore)) {
                    if (pieceOwner === previousPlayer) {
                        // Cannot return right away
                        // because the last player only wins if the other does not get an alignment
                        lastPlayerAligned5 = true;
                    }
                    else {
                        return GameStatus.getDefeat(previousPlayer);
                    }
                }
            }
        }
        if (lastPlayerAligned5) {
            return GameStatus.getVictory(previousPlayer);
        }
        return GameStatus.ONGOING;
    }
    getLegalMoves(state) {
        const moves = [];
        for (const coordAndContent of state.getCoordsAndContents()) {
            const upOrleftCord = coordAndContent.coord;
            if (upOrleftCord.x + 1 < TrexoState.SIZE) {
                const rightCoord = new Coord(upOrleftCord.x + 1, upOrleftCord.y);
                moves.push(...this.getPossiblesMoves(state, upOrleftCord, rightCoord));
            }
            if (upOrleftCord.y + 1 < TrexoState.SIZE) {
                const downCoord = new Coord(upOrleftCord.x, upOrleftCord.y + 1);
                moves.push(...this.getPossiblesMoves(state, upOrleftCord, downCoord));
            }
        }
        return moves;
    }
    getPossiblesMoves(state, first, second) {
        const firstStack = state.getPieceAt(first);
        const secondStack = state.getPieceAt(second);
        let tileHidesEntirelyOneTile;
        if (firstStack.isGround()) {
            tileHidesEntirelyOneTile = false;
        }
        else {
            tileHidesEntirelyOneTile = (firstStack.getUpperTileId() === secondStack.getUpperTileId());
        }
        const stacksAreOnUnevenGround = firstStack.getHeight() !== secondStack.getHeight();
        if (tileHidesEntirelyOneTile || stacksAreOnUnevenGround) {
            return [];
        }
        else {
            return [
                TrexoMove.from(first, second).get(),
                TrexoMove.from(second, first).get(),
            ];
        }
    }
}
